import React, { useCallback, useState } from 'react';

import {
  HTML_BLOCK_MAX_BYTES,
  htmlSourceByteLength,
  looksLikeHtmlSource,
} from '../../helpers/block-types/html';

type Props = {
  onSubmitSource: (source: string) => Promise<void> | void;
};

const MAX_MB = Math.floor(HTML_BLOCK_MAX_BYTES / (1024 * 1024));

// Paste-raw-HTML, the second entry point JOU-40 requires. Someone with a Claude
// artifact has HTML in a chat window rather than a file on disk, so this is the
// path most people will actually reach for.
export const HtmlPastePanel = ({ onSubmitSource }: Props) => {
  const [source, setSource] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = useCallback(async () => {
    const trimmed = source.trim();

    if (!trimmed) {
      setErrorMessage('Paste some HTML first.');
      return;
    }
    // Both checks mirror the server, which remains the control. Doing them here
    // turns a failed round trip into an immediate, specific message.
    if (!looksLikeHtmlSource(trimmed)) {
      setErrorMessage('That does not look like HTML.');
      return;
    }
    if (htmlSourceByteLength(trimmed) > HTML_BLOCK_MAX_BYTES) {
      setErrorMessage(`HTML must be smaller than ${MAX_MB} MB.`);
      return;
    }

    setErrorMessage('');
    setSubmitting(true);
    try {
      await onSubmitSource(trimmed);
    } catch (e: any) {
      setErrorMessage(e?.message || 'Something went wrong, please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [source, onSubmitSource]);

  return (
    <div className='flex flex-col gap-2 w-full' data-testid='html-paste-panel'>
      <textarea
        className='w-full h-40 p-2 font-mono text-bedrock-p rounded-lg border border-bedrock-light-gray'
        placeholder='Paste your HTML here'
        value={source}
        spellCheck={false}
        onChange={(e) => setSource(e.target.value)}
        aria-label='HTML source'
      />
      {errorMessage && <p className='text-bedrock-p text-bedrock-red'>{errorMessage}</p>}
      <button
        type='button'
        className='has-hover text-bedrock-p text-bedrock-black underline self-start'
        disabled={submitting}
        onClick={onSubmit}
      >
        {submitting ? 'Adding…' : 'Add HTML'}
      </button>
    </div>
  );
};

export default HtmlPastePanel;
